/**
 * The marketing site's copy, as this screen handles it.
 *
 * Everything in this file is a pure function over a document, which is what
 * lets `lib.test.ts` assert the two rules the screen exists to enforce — no
 * empty Arabic, and a warning when someone types a price — without rendering
 * anything.
 *
 * A DOCUMENT IS A TREE OF STRINGS, and every editable value is addressed by a
 * dotted path: `hero.title`, `faq.items[3].a`. That is the same notation the
 * API refuses with, so a message from the server and a field on this screen
 * name the same thing in the same words.
 */

export const CONTENT_LOCALES = ['en', 'ar'] as const;
export type ContentLocale = (typeof CONTENT_LOCALES)[number];

export type ContentNode = string | ContentNode[] | { [key: string]: ContentNode };
export type ContentDocument = { [key: string]: ContentNode };

export interface PublishedRevision {
  version: number;
  publishedAt: string;
  publishedBy: string | null;
  document: ContentDocument;
}

/** One language's state, exactly as `GET /site-content` returns it. */
export interface LocaleState {
  locale: ContentLocale;
  /** Null while the site is still serving the copy bundled into its build. */
  published: PublishedRevision | null;
  draft: ContentDocument;
  draftSource: 'draft' | 'published' | 'seed';
  draftUpdatedAt: string | null;
  draftUpdatedBy: string | null;
  dirty: boolean;
  blanks: string[];
}

export type SiteContentState = Record<ContentLocale, LocaleState>;

// ---------------------------------------------------------------------------
// paths
// ---------------------------------------------------------------------------

/** `faq.items[3].a` → `['faq', 'items', 3, 'a']`. */
export function parsePath(path: string): Array<string | number> {
  const steps: Array<string | number> = [];
  for (const part of path.split('.')) {
    const [head, ...indices] = part.split('[');
    if (head) steps.push(head);
    for (const index of indices) steps.push(Number(index.replace(']', '')));
  }
  return steps;
}

export function readPath(document: ContentNode | undefined, path: string): ContentNode | undefined {
  let node: ContentNode | undefined = document;
  for (const step of parsePath(path)) {
    if (node === undefined || typeof node === 'string') return undefined;
    node = (node as Record<string | number, ContentNode>)[step];
  }
  return node;
}

/** The string at `path`, or `''` — which is also what a missing value means here. */
export function readString(document: ContentNode | undefined, path: string): string {
  const value = readPath(document, path);
  return typeof value === 'string' ? value : '';
}

/**
 * `path` set to `value`, as a NEW document.
 *
 * Immutable because React decides whether to re-render by identity, and an
 * editor that mutates its own state in place is an editor where every third
 * keystroke does not appear.
 */
export function writePath(document: ContentDocument, path: string, value: ContentNode): ContentDocument {
  const steps = parsePath(path);

  const set = (node: ContentNode, depth: number): ContentNode => {
    if (depth === steps.length) return value;
    const step = steps[depth];
    if (typeof step === 'number') {
      const list = Array.isArray(node) ? [...node] : [];
      list[step] = set(list[step], depth + 1);
      return list;
    }
    const object = node && typeof node === 'object' && !Array.isArray(node) ? { ...node } : {};
    (object as Record<string, ContentNode>)[step] = set(
      (object as Record<string, ContentNode>)[step],
      depth + 1,
    );
    return object;
  };

  return set(document, 0) as ContentDocument;
}

/** Every string in the document, by path, in the order the page reads. */
export function leafPaths(node: ContentNode | undefined, prefix = ''): string[] {
  if (typeof node === 'string') return [prefix];
  if (Array.isArray(node)) return node.flatMap((item, i) => leafPaths(item, `${prefix}[${i}]`));
  if (!node || typeof node !== 'object') return [];
  return Object.entries(node).flatMap(([key, child]) =>
    leafPaths(child, prefix ? `${prefix}.${key}` : key),
  );
}

/** The section a path belongs to — its first segment, which is a top-level key. */
export function sectionOf(path: string): string {
  return parsePath(path)[0] as string;
}

// ---------------------------------------------------------------------------
// the two rules
// ---------------------------------------------------------------------------

/**
 * Every field a publish would refuse, by path.
 *
 * THE ARABIC HALF OF THIS IS THE POINT. tarabeeza.com holds its two languages
 * as a `Record<Locale, Copy>` with no cross-language fallback, on purpose:
 * falling back to English for a missing Arabic key is how a bilingual site ends
 * up half English in production without anyone noticing. The consequence is
 * that an empty Arabic field does not render an English sentence — it renders
 * nothing — so Publish is blocked while any exists, and the paths are listed
 * rather than counted, because "Arabic is incomplete" is not something anyone
 * can act on.
 */
export function blankPaths(document: ContentDocument): string[] {
  return leafPaths(document).filter((path) => readString(document, path).trim() === '');
}

/**
 * Does this sentence quote a price?
 *
 * THESE TWO EXPRESSIONS ARE `content.test.ts` FROM THE LANDING REPOSITORY,
 * carried across to where the decision is now made. That test still runs, and
 * still refuses a price in the copy compiled into the site — the fallback the
 * page renders when this API cannot be reached, which is the worst possible day
 * to quote an invented figure. What it can no longer do is decide what the LIVE
 * site says, because the live site says whatever is typed here.
 *
 * So the same rule lands here as a WARNING and not a refusal, for a reason
 * worth stating: the site's own FAQ answers "what does it cost" with "pricing
 * is quoted for the venue". Publishing a figure and that sentence together
 * makes the page contradict itself on the question a buyer asks first. That is
 * worth interrupting someone over. It is not worth overruling them about — it
 * is their platform, their price, and they may well have edited the FAQ answer
 * in the same sitting.
 */
const CURRENCY = /(\$|€|£|﷼|ج\.م|EGP|USD|SAR|AED)\s?\d|\d\s?(EGP|USD|SAR|AED|جنيه|ريال|درهم)/i;
const PER_MONTH = /\d+\s*(\/|per\s+)(month|mo\b|user|seat|branch)|شهريًا\s*\d/i;

export function looksLikeAPrice(value: string): boolean {
  return CURRENCY.test(value) || PER_MONTH.test(value);
}

/** Every field that reads like a price, by path. */
export function pricePaths(document: ContentDocument): string[] {
  return leafPaths(document).filter((path) => looksLikeAPrice(readString(document, path)));
}

// ---------------------------------------------------------------------------
// editing lists
// ---------------------------------------------------------------------------

/** Every string in `node`, blanked — the shape of a new list item. */
export function blankOf(node: ContentNode): ContentNode {
  if (typeof node === 'string') return '';
  if (Array.isArray(node)) return node.slice(0, 1).map(blankOf);
  return Object.fromEntries(Object.entries(node).map(([key, child]) => [key, blankOf(child)]));
}

/**
 * Append an empty item to the list at `path`.
 *
 * The new item is shaped like the one already there, so a ninth question
 * arrives with a `q` and an `a` and nothing else. A list with no items to copy
 * cannot be extended — but the API refuses an empty list anyway, so one never
 * reaches this screen.
 */
export function appendListItem(document: ContentDocument, path: string): ContentDocument {
  const list = readPath(document, path);
  if (!Array.isArray(list) || list.length === 0) return document;
  return writePath(document, path, [...list, blankOf(list[0])]);
}

export function removeListItem(document: ContentDocument, path: string, index: number): ContentDocument {
  const list = readPath(document, path);
  if (!Array.isArray(list)) return document;
  return writePath(
    document,
    path,
    list.filter((_, i) => i !== index),
  );
}

// ---------------------------------------------------------------------------
// live vs drafted
// ---------------------------------------------------------------------------

/**
 * Which fields the draft says differently from what the site is serving.
 *
 * With nothing published, EVERY field counts as changed — the site is rendering
 * the copy bundled into its own build, and pressing Publish would replace all
 * of it. Saying "0 changes" there would be the one reading that is actually
 * misleading.
 */
export function changedPaths(draft: ContentDocument, live: ContentDocument | undefined): string[] {
  const paths = leafPaths(draft);
  if (!live) return paths;
  return paths.filter((path) => readString(draft, path) !== readString(live, path));
}

/** A path with its section stripped, for a label under a section heading. */
export function fieldLabel(path: string): string {
  const section = sectionOf(path);
  const rest = path.slice(section.length).replace(/^\./, '');
  return rest || section;
}
